/**
 * WCC360 v2 — Part 1 for the 15–18 band.
 *
 * An 18-year-old is choosing between careers. A 16-year-old is choosing
 * between streams, subjects and entrance exams — decisions made two to six
 * years earlier, and much harder to undo. So Part 1 asks about school,
 * not about a working life they haven't had yet.
 *
 * X-PAY-002 (would you take less for the first year) and X-GEO-001 (where
 * can you work) are dropped: both assume earning experience nobody has at
 * sixteen. X-MOBILITY-001 replaces the geography question with the one that
 * actually constrains a teenager — how far from home they can study.
 */
import { ChoiceScreen, type Choice } from "../components/ChoiceScreen";
import { useAssessmentV2 } from "../store";
import {
  LEAN_TO_STREAM, MONEY_MATTERS_VALUE,
  type MoneyMatters, type Stream, type StreamLean, type StudyMobility,
} from "../types";
import { writeSlot, emptySlots, type SlotState } from "../slots";

interface ScreenProps { onNext: () => void }

// -----------------------------------------------------------------------
// X-QUAL-001 (restemmed) — stream
// -----------------------------------------------------------------------

export function StreamScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();

  const choices: readonly Choice<Stream>[] = [
    { value: "pcm", label: "Science (PCM)", hint: "Physics, Chemistry, Maths" },
    { value: "pcb", label: "Science (PCB)", hint: "Physics, Chemistry, Biology" },
    { value: "commerce", label: "Commerce", hint: "Business, accounts and economics" },
    { value: "arts", label: "Arts & Humanities", hint: "Languages, history, social science" },
    { value: "vocational", label: "Vocational / ITI", hint: "Hands-on skills training for a trade" },
    { value: "undecided", label: "Haven't chosen yet", hint: "Completely fine — we'll help with exactly that" },
  ] as const;

  return (
    <ChoiceScreen
      title="Which stream are you in, or heading for?"
      subtitle="Whatever you pick here, nothing gets closed off."
      choices={choices}
      value={state.part1.stream}
      onSelect={(v) => {
        setPart1({
          stream: v,
          // A stream chosen here is theirs. Only X-STREAM-002 sets provisional.
          stream_provisional: v === "undecided" ? undefined : false,
          qualification: "school",
        });
      }}
      onContinue={onNext}
      ackFor={{
        undecided: "Good — that's the decision this is built to help with.",
      }}
    />
  );
}


// -----------------------------------------------------------------------
// X-STREAM-002 (new) — provisional stream, only when nothing is chosen
// -----------------------------------------------------------------------

export function StreamLeanScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();

  const choices: readonly Choice<StreamLean>[] = [
    { value: "maths_physics", label: "I'm strongest in maths and physics" },
    { value: "bio_chem", label: "I'm strongest in biology and chemistry" },
    { value: "business", label: "I like business, accounts and economics" },
    { value: "humanities", label: "I like languages, history and the arts" },
    { value: "hands_on", label: "I learn best by doing, not by exams" },
    { value: "none", label: "Honestly, none of them" },
  ] as const;

  return (
    <ChoiceScreen
      title="No problem — that's the decision this is meant to help with. Which of these sounds most like school for you right now?"
      subtitle="This gives us somewhere to start. It's not you choosing a stream."
      choices={choices}
      value={state.part1.stream_lean}
      onSelect={(v) => {
        const provisional = LEAN_TO_STREAM[v];
        setPart1({
          stream_lean: v,
          stream: provisional,
          // Everything downstream must label this as leaning, never chosen.
          stream_provisional: provisional !== "undecided",
        });
      }}
      onContinue={onNext}

      ackFor={{
        none: "That's an honest answer, and a common one. We'll keep it wide open.",
      }}
    />
  );
}

// -----------------------------------------------------------------------
// X-PAY-001 (restemmed) — relative money only
//
// No LPA figures. A teenager has no anchor for an absolute salary, and a
// band here would be a guess dressed as data. Same slot as the 18+ band
// (prefIncomeUpside), same 0–100 scale, so W-OFFER-001 still corroborates.
// -----------------------------------------------------------------------

export function MoneyMattersScreen({ onNext }: ScreenProps) {
  const { state, setPart1, setPart2 } = useAssessmentV2();
  const slots = (state.part2.slots as SlotState) ?? emptySlots();

  const choices: readonly Choice<MoneyMatters>[] = [
    { value: "most", label: "No — earning well matters most" },
    { value: "bit_less", label: "I'd take a bit less" },
    { value: "lot_less", label: "I'd take a lot less for the right work" },
    { value: "not_thinking", label: "Money isn't what I'm thinking about" },
  ] as const;

  return (
    <ChoiceScreen
      title="Would you be OK earning less if it meant doing work you love?"
      subtitle="There's no right answer here. Both are reasonable ways to live."
      choices={choices}
      value={state.part1.money_matters}
      onSelect={(v) => {
        setPart1({ money_matters: v });
        setPart2("slots", writeSlot(slots, "prefIncomeUpside", "X-PAY-001", MONEY_MATTERS_VALUE[v], {
          label: "How much money matters",
          disagreeThreshold: 40,
        }));
      }}
      onContinue={onNext}

      ackFor={{
        most: "Straight answer — noted.",
        not_thinking: "Fair. We won't push you towards work just because it pays.",
      }}
    />
  );
}

// -----------------------------------------------------------------------
// X-MOBILITY-001 (new) — study mobility, replacing X-GEO-001
// -----------------------------------------------------------------------

export function StudyMobilityScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();

  const choices: readonly Choice<StudyMobility>[] = [
    { value: "world", label: "Anywhere in the world" },
    { value: "india", label: "Anywhere in India" },
    { value: "state", label: "Anywhere in my state" },
    { value: "home", label: "Only where I live now" },
    { value: "unsure", label: "Not sure yet" },
  ] as const;

  return (
    <ChoiceScreen
      title="For the right course, could you study away from home?"
      subtitle="This is about what's possible, not what you'd prefer."
      choices={choices}
      value={state.part1.study_mobility}
      onSelect={(v) => setPart1({ study_mobility: v })}
      onContinue={onNext}

      ackFor={{
        world: "Big reach. We'll keep international routes and travel-heavy work in the mix.",
        unsure: "That's fine. It's a conversation for later, with the people at home.",
      }}
    />
  );
}
